import { useEffect } from 'react';
import { Car, Flag } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/Shared/Button';
import { useTripDialogs } from '@/hooks/useTripDialogs';
import { useMyVehicleTripsStore } from '@/store/useMyVehicleTripsStore';
import { formatDateTime, parseUtcTimestamp } from '@/utils/dateUtils';
import { DRIVER, needsArrivalTime } from '@/utils/vehicleTrips';
import { EndTripDialog } from './EndTripDialog';

const DATE_TIME: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * An own trip still running from an earlier day (FAFD-68) — an open item like a missing
 * clock-out: "Fahrt läuft seit …" with "Fahrt beenden", which asks for the arrival. Today's
 * running trip is no open item; the trip menu shows it. Shows nothing otherwise.
 */
export function RunningTripNotice() {
  const { t } = useTranslation('vehicles');
  const current = useMyVehicleTripsStore((s) => s.current);
  const saving = useMyVehicleTripsStore((s) => s.saving);
  const fetchCurrent = useMyVehicleTripsStore((s) => s.fetchCurrent);
  const endTrip = useMyVehicleTripsStore((s) => s.endTrip);
  const { askEnd, endDialog } = useTripDialogs();

  useEffect(() => {
    void fetchCurrent();
  }, [fetchCurrent]);

  if (!current || current.role !== DRIVER || !needsArrivalTime(current.startedAt, new Date())) return null;

  const end = async () => {
    const answer = await askEnd('end', current);
    if (answer) await endTrip(answer.endTrip ?? {});
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:bg-amber-900/10 dark:border-amber-800"
      role="status"
    >
      <Car className="w-4 h-4 shrink-0 text-amber-600" strokeWidth={2} aria-hidden />
      <span className="flex-1 min-w-0 text-[13px] text-amber-900 dark:text-amber-200 tabular-nums">
        <span className="font-mono">{current.licensePlate}</span>
        {' · '}
        {t('trip.runningSince', { time: formatDateTime(parseUtcTimestamp(current.startedAt), DATE_TIME) })}
      </span>
      <Button
        size="sm"
        variant="secondary"
        onClick={end}
        disabled={saving}
        leftIcon={<Flag className="w-3.5 h-3.5" strokeWidth={2} />}
      >
        {t('trip.end')}
      </Button>
      <EndTripDialog {...endDialog} busy={saving} />
    </div>
  );
}
